import { z } from 'zod';
import { CARD_TITLE_MAX_LENGTH } from './board';
import { cardDateInstantSchema } from './card-dates';
import { ulidSchema } from './id';
import { RANK_MAX_LENGTH } from './rank';

/**
 * Checklists — the contract layer for block **B26**.
 *
 * `BE-26.1` (`FR-CARD-016`, add, rename, reorder and delete a checklist on a
 * card), `BE-26.2` (`FR-CARD-017`, its items: add, edit, tick, reorder,
 * delete), `BE-26.3` (`FR-CARD-019`, copy the items of an existing checklist
 * into a new one) and the server half of `FR-CARD-018` (the per-user
 * hide-checked preference — see `checklistSchema.hideChecked`).
 *
 * ---------------------------------------------------------------------------
 * TWO ROUTE FILES, ONE CONTRACT FILE — `./board-archive`'s SHAPE, NOT A NEW
 * ONE
 * ---------------------------------------------------------------------------
 * `apps/server/src/routes/checklists.ts` (six routes on the checklist) and
 * `apps/server/src/routes/checklist-items.ts` (five on its items) share this
 * one file, on `./board-archive`'s precedent: the two describe **one subsystem
 * from its two ends**, and neither is independently useful. A client cannot
 * render a checklist without the item shape, and cannot move an item without
 * knowing which checklist ids the read handed it. Splitting them would put
 * `checklistItemSchema` in one file and `checklistSchema` — which *nests* it —
 * in another, with an import edge and nothing gained.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THIS FILE DECLARES NO NOUN OF `BE-02.1`'s EIGHT
 * ---------------------------------------------------------------------------
 * `BE-02.1`'s item line names eight nouns: workspace, board, list, card,
 * comment, label, membership, notification. **A checklist is not one of
 * them**, and "it is a real table with real columns" is exactly the reasoning
 * that has made this count wrong repeatedly — `./board-filter`, `./search`,
 * `./cross-board-move`, `./board-archive`, `./boards-page`, `./card-cover` and
 * `./card-dates` have each had to record the same correction. Seven of the
 * eight have a file; **membership** is still the one that does not, and
 * `ls packages/shared/src` is still the instruction rather than this sentence.
 *
 * ⚠ **No ordinal is claimed for that ("the seventh time…"), and the omission is
 * deliberate rather than lazy.** A draft of this header said *seventh* and
 * listed six files, having missed `./card-cover`, which makes the same claim in
 * different words — caught before it shipped, and exactly the drift the list
 * above warns about. `./index`'s own B26 entry refuses the ordinal for the same
 * reason and says so; grep for the claim if the tally is wanted.
 *
 * What is genuinely new here: this is the first file in the package that
 * declares a **container and its contents** — `checklistSchema` nests
 * `checklistItemSchema` and the nesting is not optional (see below). Every
 * previous nesting in this package references something that stands alone
 * elsewhere: `cardListRefSchema` narrows a list six of B11's routes serve,
 * `cardLabelSchema` narrows a label `GET /boards/:id/labels` serves,
 * `publicProfileSchema` is an account. **No read anywhere returns a checklist
 * item independently of the checklist that owns it.** The one route that puts
 * an item on the wire outside `checklistSchema.items` is
 * `POST /checklists/:id/items`, which answers with the items *that request
 * just created* — a receipt, not a listing (see `checklistItemListSchema`,
 * which says at length why a client that treats it as one loses data).
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ ONE VIEW SHAPE, WHERE `./label` HAS TWO — AND THE DIFFERENCE IS SCALE,
 * NOT TASTE
 * ---------------------------------------------------------------------------
 * `./label` declares `labelSchema` (seven fields, the board's set) *and*
 * `cardLabelSchema` (three fields, the copy that rides on every card front),
 * because a card's labels travel on `GET /boards/:id/contents` — "the one
 * response in the product that scales with the size of the board", up to a
 * couple of thousand label objects in one payload.
 *
 * **No checklist ever rides the board face.** `FR-CARD-018`'s card front
 * carries an *aggregate badge* — two integers, `checklistItemCount` and
 * `checklistCheckedItemCount` on `boardFaceCardSchema` in `./board-contents` —
 * and never a checklist, never an item, never a title. So the only surface
 * this shape appears on is a **single card's back** (`GET /cards/:id`,
 * `GET /cards/:id/checklists` and the four routes that answer with one
 * checklist), where the population is one card's checklists and their items.
 * A narrowed second view would be a shape kept in step with the first by hand
 * for no payload saved.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THERE IS NO `itemCount` AND NO `checkedItemCount` ON `checklistSchema`,
 * AND THAT IS A DECISION — READ IT BEFORE ADDING THEM BACK
 * ---------------------------------------------------------------------------
 * `FR-CARD-018` opens with "Each checklist shows a `checked/total` counter",
 * so a pair of count fields is the obvious shape and this file deliberately
 * does not have one. Both numbers are `items`' own: `total` is `items.length`
 * and `checked` is `items.filter((i) => i.checked).length`, computed by the
 * client from a payload it already holds. Shipping them beside the array would
 * make **disagreement representable** — a response saying `itemCount: 5` over
 * four items is a bug no schema, status code or test that reads only the
 * response can catch — and that is the exact class of defect `BE-25.1` closed
 * by shipping `members` as an array with no `memberCount` beside it, and that
 * `boardFaceListSchema.openCardCount` establishes the *counter*-precedent for.
 *
 * ⚠ **`openCardCount` is the one that looks like it licenses the counts, and
 * it does not.** It exists because `cards` will one day be a *page*:
 * `FR-BOARD-035`/`BE-47.4` caps a board at 5,000 open cards and the block that
 * enforces it will stop `GET /boards/:id/contents` serving every card in a
 * list — at which point the count stays whole while the array is truncated,
 * and the field is the only thing that keeps the badge honest. **Nothing of
 * the kind is scheduled for checklist items.** `FR-CARD-016` records that
 * "Trello documents no limit on checklists per card", `FR-CARD-017` names no
 * item limit, and the `FR-PLAN-*` corpus that would hold one is absent from
 * the requirements on disk (`CLAUDE.md`'s ground-truth table) — so there is no
 * truncation for a count to survive, and the count would be pure duplication
 * from the day it shipped.
 *
 * ⚠ **The premise the drop rests on, stated so a route agent cannot break it
 * accidentally: `items` is REQUIRED on every response carrying a
 * `Checklist`.** `POST /cards/:id/checklists`, `PATCH /checklists/:id/title`,
 * `POST /checklists/:id/move` and `PUT /checklists/:id/hide-checked` all
 * answer with this shape, so each must read the checklist's items — there is
 * no "the client already has them" shortcut, because the array is now the
 * *only* source of both counters and a route that omitted it would be
 * shipping a checklist whose progress counter reads 0/0. `z.object` **strips**
 * an unknown key but a *missing required* key is a genuine parse failure, so
 * this one fails loudly rather than silently. (`GET`/`DELETE` are the two
 * exceptions that prove it: the read returns the same full shape, and
 * `DELETE /checklists/:id` answers `204` with no body at all —
 * `DELETE /lists/:id`'s precedent.)
 *
 * ⚠ **Where the counts DO live, so their absence here reads as a decision.**
 * (a) `boardFaceCardSchema.checklistItemCount` / `.checklistCheckedItemCount`
 * in `./board-contents` — the `FR-CARD-018` AC4 card-front badge ("a card with
 * two checklists totalling 7 items of which 3 are checked … shows a checklist
 * badge of 3/7"), summed across **all** the card's checklists. Those cannot be
 * derived, because the board face ships no checklists array to derive them
 * from — which is the same test `commentCount` passes one field over, and the
 * reason the two shapes differ is not inconsistency but that one of them
 * carries the items and the other does not. ⚠ The server must cast
 * `count(*)::int` there: `count(*)` is bigint, `pg` hands it back as a
 * **string**, and `z.number().int()` would 500 the happy path on every board
 * (B25's recorded trap). (b) `checklist.removed`'s event payload
 * (`{ checklistId, title, itemCount, checkedItemCount }`, `./events`), read
 * inside the transaction *before* the hard delete — an append-only row must
 * carry what the deleted rows no longer can. Neither is a precedent for
 * putting them on this view.
 *
 * ⚠ **The one change that would require them back, in the same edit:** a block
 * that makes the server honour `hideChecked` by *filtering* checked items out
 * of `items`. The moment the array stops being the complete set, both counters
 * become underivable and both fields must be added in that change — not
 * afterwards, when a client is already computing them from a filtered array
 * and reporting 3/3 on a checklist of five. Nothing in B26 does this; see
 * `hideChecked`.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `hideChecked` IS THE FIRST PER-CALLER FIELD IN THIS PACKAGE ON A SHARED
 * ROW
 * ---------------------------------------------------------------------------
 * Every other field on every other view here is a property of the row: two
 * members reading one card get byte-identical payloads. `hideChecked` is read
 * from `checklist_hide_checked_prefs` **for the calling account only**
 * (`FR-CARD-018`'s edge case: "the hide-checked preference is per user per
 * checklist and must not change what other members see"), so two members
 * reading one checklist legitimately get different bytes for one row. Two
 * consequences a client must know and a route must not "fix":
 *
 *   - **It is not cacheable across accounts.** A shared client cache keyed on
 *     the card id alone will show one member another member's preference.
 *   - **`false` is the answer for "no row" AND for an anonymous caller**, and
 *     the two are deliberately indistinguishable. A signed-out visitor reading
 *     a public board's card has no account for a preference to belong to;
 *     `null` would be a second spelling of a state the boolean already covers,
 *     on `./user`'s "one state, one representation" rule.
 *
 * `./notification` is not a counter-example: a notification *row* belongs to
 * one account, so its whole payload is per-user by construction. This is one
 * field of a shared row differing by reader, which is new.
 *
 * ⚠ **It changes nothing the server sends.** `items` is always the complete,
 * unfiltered set in `position, id` order; `hideChecked` is a **rendering
 * instruction** `FE-26.2` obeys, and the requirement's own words are that the
 * toggle "removes checked items from view **without changing their state**"
 * and "indicates how many are hidden" — a number the client can only produce
 * if it was given the checked items in the first place.
 *
 * ---------------------------------------------------------------------------
 * WHAT THIS FILE DELIBERATELY DOES NOT DECLARE
 * ---------------------------------------------------------------------------
 *   - **`checklist_items.assignee_user_id` and `checklist_items.due_at`.** Both
 *     columns have existed since `0003_card_detail.sql` and both belong to
 *     `FR-CARD-020` (a member and a due date on an item), which B26 does not
 *     build. Their absence here is what makes `FR-CARD-019`'s amended edge
 *     case true on the wire as well as in the handler — "a copy carries item
 *     **text** and **order**; every other per-item field is left empty on the
 *     new items, including the assignee and the due date `FR-CARD-020`
 *     defines" — since a shape that cannot express them cannot copy them.
 *   - **`checklist_items.converted_to_card_id`.** `FR-CARD-021` (convert an
 *     item into a card) is **B62's**, Parity-Later, and `./events` withholds
 *     `checklist.item_converted_to_card` from its registry for the same
 *     reason: the block that first emits a placeholder is the block that
 *     claims it.
 *   - **A per-card checklist cap or a per-checklist item cap.**
 *     `FR-CARD-016`'s behaviour text is explicit that no limit is documented,
 *     and the `FR-PLAN-*` family that would impose one does not exist on disk.
 *     Flagged, not invented — `implementation-plan.md` §9. See
 *     `CHECKLIST_ITEM_PASTE_MAX_ITEMS` for the one bound that *is* declared
 *     and why it is not this.
 *   - **A `checklist.restored` shape, a `deleted_at`, or an archive state.**
 *     `FR-CARD-016`'s edge case: "deletion is permanent — checklists are not
 *     archived and cannot be restored." There is no `checklists.deleted_at`
 *     column and no `isLiveChecklist()` anywhere in the server; deletion is
 *     **hard**, `fk_checklist_items_checklist_id` is `ON DELETE CASCADE`, and
 *     `activity_entries` has no `checklist_id` column and no foreign key to
 *     either table, so the events survive their own subject (`label.deleted`'s
 *     precedent, and `./events`' `checklist.removed` argues it in full).
 *   - **A cross-CARD item move.** `FR-CARD-017` confines the drag to
 *     "checklists on the same card"; see `checklistItemMoveBodySchema`, which
 *     is shaped so the request cannot even be *asked*.
 *
 * The file follows the division every feature file here follows — **input
 * primitives**, which validate and normalise what a route accepts, and
 * **views**, which describe what a route returns. Both text fields trim and
 * refuse empty on the way in and are width-bounded and refuse nothing on the
 * way out.
 */

// ---------------------------------------------------------------------------
// Column widths, and one request-shape bound. See each constant for which it
// is — `./card`'s rule is that the two kinds must never be confused, let alone
// shared.
// ---------------------------------------------------------------------------

/**
 * `checklists.title varchar(255) NOT NULL` — a real column width, measured
 * against `db/postgres/migrations/0003_card_detail.sql`. Over it is `value too
 * long for type character varying(255)` at INSERT, so losing this bound is an
 * ugly 500 rather than a cap quietly ceasing to exist.
 *
 * Declared rather than imported, on `./card`'s standing rule: *import the
 * constant that describes **your** column if one exists, declare it if none
 * does, and never share one across two columns.* Nothing in this package
 * describes `checklists.title` — `LABEL_NAME_MAX_LENGTH` is `labels.name` and
 * is the same number for a different column, `LIST_TITLE_MAX_LENGTH` (512) is
 * `lists.title`, `CARD_TITLE_MAX_LENGTH` (512) is `cards.title`. Sharing
 * `LABEL_NAME_MAX_LENGTH` here would typecheck, read tidily, and silently bind
 * two requirements' columns together the day one of them is widened.
 *
 * Counted in **UTF-16 code units**, because that is what `z.string().max()`
 * measures and what a browser's `input.value.length` reports.
 */
export const CHECKLIST_TITLE_MAX_LENGTH = 255;

/**
 * `checklist_items.text varchar(2048) NOT NULL` — again a real column width,
 * and again declared rather than shared. It is **per item**, never per
 * request: a multi-line paste of forty lines is forty items each bounded by
 * this, not one 2,048-character body. See `checklistItemCreateBodySchema`,
 * where that distinction is the whole of the validation.
 *
 * Counted **after trimming**, because `checklistItemTextSchema` trims before
 * it measures — `./comment`'s `COMMENT_BODY_MAX_LENGTH` note, and the pair of
 * assertions that pins it is in `checklist.test.ts` (a 2,048-character string
 * wrapped in whitespace must PASS; 2,049 visible characters must fail —
 * either one alone cannot tell trim-then-measure from measure-then-trim).
 */
export const CHECKLIST_ITEM_TEXT_MAX_LENGTH = 2048;

/**
 * The most items **one request** may create — `FR-CARD-017`'s multi-line
 * paste, bounded.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ THIS IS A REQUEST-SHAPE BOUND, NOT A LIMIT ON WHAT A CHECKLIST MAY HOLD,
 * AND THE DISTINCTION IS WHY IT EXISTS AT ALL
 * ---------------------------------------------------------------------------
 * `./board-archive`'s `ARCHIVE_PAGE_SIZE_MAX` and `./comment`'s
 * `COMMENT_PAGE_SIZE_MAX` are the precedent: bounds over the **shape of one
 * request**, not over anybody's data. A member who pastes 300 lines pastes
 * twice and ends up with 300 items; the checklist itself has **no cap** and
 * this file declares none (`FR-CARD-016`: "Trello documents no limit").
 *
 * ⚠ **`./board-filter`'s refusal to cap its facets is the precedent that does
 * NOT reach this, and the reason is worth stating because it is the nearer of
 * the two by appearance.** B19 declined a cap on `?members=`/`?labels=` on two
 * grounds: any number would be invented, *and* Node's 16 KB header budget
 * "refuses the URL with a **431** at roughly 470 ids **long before anything in
 * the database is reached**" — a backstop that fires before the damage. There
 * is no equivalent here. The only backstop on a request body is Fastify's own
 * body limit, this server sets no `bodyLimit` of its own (so the framework
 * default applies — a number that lives in the server's configuration and is
 * deliberately not restated here), and at *any* such limit a body of newlines
 * reaches the handler and becomes one transaction inserting a row per line.
 * The backstop fires after the damage, not before it, so B19's reasoning
 * genuinely does not transfer and a bound is owed.
 *
 * ⚠ The test that separates the two precedents, and the one to apply to the
 * next cap this package is tempted by: **does the bound change what a member
 * can end up with, or only how many requests it takes them?** A filter facet
 * capped is a question a member may no longer ask. A paste capped is a paste
 * repeated.
 *
 * 200 is chosen as the smallest round number no human paste plausibly reaches
 * — `FR-CARD-017` AC2's own worked example is three lines — while keeping one
 * transaction's write bounded. It is not derived from any requirement, because
 * none names one; if a real per-checklist limit is ever specified it belongs
 * to the absent `FR-PLAN-*` family and is a different constant with a
 * different meaning, never this one widened.
 */
export const CHECKLIST_ITEM_PASTE_MAX_ITEMS = 200;

// ---------------------------------------------------------------------------
// The multi-line paste rule, as a pure function.
// ---------------------------------------------------------------------------

/**
 * `FR-CARD-017`'s paste rule: "Pasting multi-line text creates one item per
 * line", with its edge case "an empty item cannot be created; blank input is
 * ignored".
 *
 * Splits on line breaks, trims each line, **drops** the blank ones and
 * preserves the order of what survives. `'a\n\nb'` is two items, not three
 * with an empty one in the middle.
 *
 * ---------------------------------------------------------------------------
 * WHY IT IS PURE, EXPORTED AND HERE RATHER THAN IN THE ROUTE
 * ---------------------------------------------------------------------------
 * It is the whole of `FR-CARD-017`'s paste behaviour and it decides nothing
 * else — no query, no permission, no clock — so it is assertable with no
 * container running, which is `matchesDestinationLabel`'s
 * (`apps/server/src/cards/reconcile.ts`) and `mayDeleteComment`'s footing. It
 * lives in `@molt/shared` rather than in `apps/server` because both ends need
 * the same answer: `FE-26.1`'s composer shows the member how many items a
 * paste is about to create, and if it split differently from the server the
 * preview would be a lie no test could catch.
 *
 * ⚠ **The `\r\n` case is the one that silently produces empty items.** The
 * pattern is `/\r\n|\r|\n/` and the alternation order is load-bearing: with
 * `/\n|\r\n|\r/` a Windows paste splits at the `\n` first and leaves a
 * trailing `\r` on every line, which `.trim()` then removes — so the *visible*
 * outcome is identical and nothing fails. It stops being identical the day the
 * trim is removed or reordered, which is exactly the kind of change a later
 * pass makes while "simplifying". A `\r`-only paste (classic Mac, and what
 * some clipboard bridges still emit) is the case the naive pattern gets
 * outright wrong: it splits nowhere and produces **one** item containing every
 * line.
 *
 * ⚠ **It never throws and it may return `[]`.** A wholly blank body is a
 * caller error, but it is the *route's* 422 (through
 * `checklistItemCreateBodySchema`, which runs this function inside a
 * refinement), not this function's exception — a pure rule that throws cannot
 * be used by a client to preview anything.
 *
 * Trimming is per line and touches only the ends: interior whitespace is
 * preserved, so `'  step  one  '` becomes `'step  one'` and not `'step one'`.
 */
export function splitChecklistItemLines(text: string): string[] {
  return text
    .split(/\r\n|\r|\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

// ---------------------------------------------------------------------------
// INPUT primitives — what a route ACCEPTS.
// ---------------------------------------------------------------------------

/**
 * A checklist title as `checklists.title` stores it — trimmed, non-empty, at
 * most `CHECKLIST_TITLE_MAX_LENGTH`.
 *
 * `.trim().min(1).max()` is `listTitleSchema`'s, `boardTitleSchema`'s and
 * `commentBodySchema`'s idiom, reused rather than re-decided. `''` and `'   '`
 * are both 422s: `checklists.title` is `NOT NULL` and there is no "unnamed
 * checklist" state for an empty string to mean — which is the opposite of
 * `labelNameSchema`, where `null` is a real state the column models and the
 * trim exists to keep `''` from becoming a second spelling of it.
 *
 * ⚠ **No default title.** `FR-CARD-016`: "Adding one prompts for a title", and
 * AC1's worked example names it. A server-side `'Checklist'` default would be
 * a title the member never chose, appearing in `checklist.added`'s payload and
 * in the card's activity as though they had typed it.
 */
export const checklistTitleSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHECKLIST_TITLE_MAX_LENGTH);

/**
 * One checklist item's text as `checklist_items.text` stores it — trimmed,
 * non-empty, at most `CHECKLIST_ITEM_TEXT_MAX_LENGTH`.
 *
 * ⚠ **This is the schema for ONE item, and no route accepts it directly.**
 * `POST /checklists/:id/items` takes a whole paste (see
 * `checklistItemCreateBodySchema`) and `PATCH /checklist-items/:id/text` takes
 * one item's replacement text (`checklistItemTextPatchSchema`, which uses this
 * verbatim). Exported because both bodies and both ends of the paste rule are
 * measured against it, and because a route inserting split lines must be able
 * to state the bound it is inserting under.
 *
 * The trim is the reason `FR-CARD-017`'s "blank input is ignored" is
 * expressible at all: because the boundary trims before it measures, `'   '`
 * and `''` are the same refusal rather than one refusal and one row holding
 * three spaces.
 */
export const checklistItemTextSchema = z
  .string()
  .trim()
  .min(1)
  .max(CHECKLIST_ITEM_TEXT_MAX_LENGTH);

/**
 * One end of the gap a checklist is dropped into — a neighbouring checklist's
 * id, or `null` for an open end.
 *
 * `previousChecklistId`/`nextChecklistId`, never `before`/`after`:
 * `listNeighbourSchema` in `./list` states the argument in full and it is
 * inherited verbatim here, because the naming matches `rankBetween(before,
 * after)`'s argument order and the rejected spelling is ambiguous in the worst
 * way — "insert *before* X" and "the checklist *before* X" name opposite
 * positions, both readings are natural, and picking the wrong one puts the
 * checklist on the wrong side of its neighbour with no error anywhere.
 *
 * `.nullable().default(null)`, so an omitted key and an explicit `null` are
 * the same request. There is no `position` field on any body in this file:
 * SC-2 and `./rank`'s header — no caller ever computes a rank, the server
 * mints one inside the transaction from ranks it read there.
 */
const checklistNeighbourSchema = ulidSchema.nullable().default(null);

/** One end of the gap a checklist ITEM is dropped into. See above. */
const checklistItemNeighbourSchema = ulidSchema.nullable().default(null);

/**
 * `POST /cards/:id/checklists` — `FR-CARD-016` AC1 (add a checklist) and
 * `FR-CARD-019` (seed it from an existing one). **One route for both**,
 * because `FR-CARD-019`'s own words make the copy a field of the add dialog:
 * "the add-checklist dialog includes a 'Copy items from' picker", and AC4
 * ("given the user chooses no source … an empty checklist is created") makes
 * the no-source case the same gesture rather than a different one.
 *
 * ⚠ **No `cardId`.** The card is the path segment, so a body `cardId` beside
 * a path `:id` would be two spellings of one fact that can disagree, and the
 * route would have to decide which wins — `createLabelBodySchema`'s argument,
 * and the deliberate divergence from `createListBodySchema`/
 * `createCardBodySchema`, whose bodies name their parent because their paths
 * do not.
 *
 * ⚠ **No neighbours and no `position`.** `FR-CARD-016` says checklists are
 * "ordered as created and reorderable" — created means **appended**, and the
 * reorder is `POST /checklists/:id/move`'s own gesture. `createLabelBodySchema`
 * made the same call for the same reason: a requirement that specifies no
 * insert-into-a-gap creation should not have one invented for it.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ `sourceChecklistId` IS THE WHOLE OF `BE-26.3`, AND THERE IS NO SECOND
 * FIELD BESIDE IT
 * ---------------------------------------------------------------------------
 * There is **no `copyCheckedState` toggle and no way for this body to ask for
 * one**, and that is `FR-CARD-019`'s settled answer rather than an omission:
 * its behaviour text and AC2 both say copied items are created unchecked, and
 * this block's D2 records the ruling against `FR-TMPL-010`'s template
 * instantiation, which is a different surface with its own undecided question.
 * A boolean here would put on the wire a request the owning requirement
 * refuses to honour.
 *
 * ⚠ **The source may be a checklist on ANOTHER card, and the scope is the
 * route's, not this schema's.** `FR-CARD-019` admits "checklists on the
 * current card and on other cards the user can reach"; a Zod schema cannot ask
 * whether the caller can read the source's card, so the rule lives inside the
 * query (SC-5, composed from the card-reader predicate — never a candidate
 * list assembled first and filtered afterwards). A source the caller cannot
 * read is refused **indistinguishably from one that does not exist** —
 * `FR-CARD-019`'s own AC5 — so this field can never be an existence oracle
 * over checklist ids.
 *
 * ⚠ **`FR-TMPL-017`'s narrower same-board picker is not contradicted by
 * this.** That requirement describes a *dialog* confined to the current
 * location scope, which is `FE-26.3`'s property; the server's rule is the
 * wider read scope above, and `FR-CARD-019`'s amended edge case records that
 * the offer may legitimately be narrower than the rule (D1, D3).
 */
export const checklistCreateBodySchema = z.strictObject({
  title: checklistTitleSchema.describe(
    'The checklist title (FR-CARD-016 AC1). Trimmed; an empty or ' +
      'whitespace-only submission is refused (422). Required — there is no ' +
      'server-side default, because a title the member did not choose would ' +
      "still appear in the card's activity as though they had.",
  ),
  sourceChecklistId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'A checklist to copy the items of (FR-CARD-019) — on this card or on ' +
        'any card the caller can read. Its item TEXT and ORDER are copied and ' +
        'nothing else: copied items are always created unchecked, with no ' +
        'assignee and no due date, and there is no toggle. Omit the key (or ' +
        'send null) for an empty checklist. A source on a card the caller ' +
        'cannot read is refused exactly as a nonexistent one is.',
    ),
});

export type ChecklistCreateBody = z.infer<typeof checklistCreateBodySchema>;

/**
 * `PATCH /checklists/:id/title` — `FR-CARD-016`, "titles can be edited
 * inline". SC-4, field-scoped.
 *
 * The title moves on its own and can move nothing else — never a
 * `PUT /checklists/:id` carrying title and position together.
 * `listTitlePatchSchema` gives the general argument; the specific cost here is
 * that the two gestures emit **different events** (`checklist.renamed` and
 * `checklist.position_changed`) that `FR-COLLAB-018`'s feed must render as
 * different sentences, and a combined body could not say which had happened.
 *
 * ⚠ **A rename to the title the checklist already carries is a legal no-op
 * that writes NO activity row**, and this schema cannot express that: the
 * comparison is between the *trimmed* incoming title and the row's pre-image
 * inside the transaction, so only the handler can make it. A handler that
 * emitted unconditionally would answer `200` with a perfectly correct body
 * while filling an append-only feed with renames nobody performed — B12's
 * drop-in-place lesson, recorded here because this is where a reader looks
 * first. `checklists.updated_at` will not betray it either:
 * `trg_checklists_set_updated_at` fires `WHEN (OLD.* IS DISTINCT FROM NEW.*)`,
 * so a no-change write leaves it byte-identical.
 */
export const checklistTitlePatchSchema = z.strictObject({
  title: checklistTitleSchema.describe(
    'The new checklist title. Trimmed; an empty or whitespace-only ' +
      'submission is refused (422) and the previous title stands. Renaming a ' +
      'checklist to the title it already has is a legal no-op and records ' +
      'nothing.',
  ),
});

export type ChecklistTitlePatch = z.infer<typeof checklistTitlePatchSchema>;

/**
 * `POST /checklists/:id/move` — `FR-CARD-016`, "ordered as created and
 * reorderable". SC-2's fractional rank doing the work.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ BOTH NEIGHBOURS NULL IS A 422 HERE, AND IT IS **LEGAL** ON
 * `checklistItemMoveBodySchema` BELOW. THE DIVERGENCE IS THE MOST
 * HARMONISABLE-LOOKING THING IN THIS FILE AND HARMONISING IT BREAKS A
 * REQUIREMENT IN EITHER DIRECTION.
 * ---------------------------------------------------------------------------
 * This body carries **no destination of its own** — a checklist belongs to the
 * card in the path and cannot move to another one — so `{ previousChecklistId:
 * null, nextChecklistId: null }` would say "move this checklist to nowhere",
 * which is not a request anybody can have meant. The only alternative readings
 * are "leave it where it is" (a silent success for a mutation that did not
 * happen) and "send it to one end" (a guess about which end). That is
 * `listPositionPatchSchema`'s reasoning in `./list`, inherited whole: one
 * neighbour is enough and is the honest shape of the two end positions —
 * `{ nextChecklistId }` alone moves it to the top, `{ previousChecklistId }`
 * alone to the bottom.
 *
 * The item body one declaration down reads the same pair of nulls as **"the
 * bottom of the named checklist"**, and correctly, because it *has* a
 * destination field: see its own docstring, which argues the other half.
 *
 * The refusal message names both fields, which is required rather than
 * courteous — `app.ts`'s error handler maps only `issue.message` into
 * `details`, so a default refinement message reaches the caller as a refusal
 * that never says what was omitted.
 *
 * ⚠ **A drop in place emits nothing.** Dropping a checklist back between the
 * neighbours it already sits between is a no-op that must write no activity
 * row, detected by comparing the checklist's current rank against the
 * **resolved** gap rather than against the raw body — `FR-BOARD-025`'s edge
 * case applied to this subject by analogy, and the handler's alone to make.
 */
export const checklistMoveBodySchema = z
  .strictObject({
    previousChecklistId: checklistNeighbourSchema.describe(
      'The checklist immediately ABOVE the gap this one is dropped into. ' +
        'Send it alone to move the checklist to the bottom of the card.',
    ),
    nextChecklistId: checklistNeighbourSchema.describe(
      'The checklist immediately BELOW the gap this one is dropped into. ' +
        'Send it alone to move the checklist to the top of the card.',
    ),
  })
  .refine((body) => body.previousChecklistId !== null || body.nextChecklistId !== null, {
    error:
      'at least one of previousChecklistId or nextChecklistId must name a neighbouring ' +
      'checklist: send previousChecklistId alone to move it to the bottom, ' +
      'nextChecklistId alone to move it to the top, or both to drop it into the gap ' +
      'between them.',
  });

export type ChecklistMoveBody = z.infer<typeof checklistMoveBodySchema>;

/**
 * `PUT /checklists/:id/hide-checked` — `FR-CARD-018`'s per-user preference,
 * the one part of that requirement that is server work at all (the progress
 * bar and the toggle control are `FE-26.2`'s).
 *
 * ⚠ **`PUT`, not `PATCH`, and one field.** The resource is the caller's whole
 * preference for this checklist — a single boolean row in
 * `checklist_hide_checked_prefs` keyed `(user_id, checklist_id)` — so a
 * full-replace verb says exactly what happens, and there is no partial state
 * for SC-4 to scope. `PUT /cards/:id/cover`'s precedent, one table over.
 *
 * ⚠ **It writes NO activity entry**, and the reasoning is recorded here as
 * well as in `./events` because this is the mutation a later reader will
 * assume was forgotten. Read state that only the caller can observe is not
 * what SC-1's audit argument is about — that argument is about facts other
 * people can see and dispute, and `FR-CARD-018`'s own edge case says this
 * preference "must not change what other members see". B17 made the identical
 * call for `notifications.read_at`. `ck_activity_entries_scope` would refuse
 * such a row anyway (it names a checklist and a user, and no card or board of
 * its own), which **confirms** the decision and must not lead it.
 *
 * ⚠ **Re-sending the value already stored is a legal no-op.** It is also not
 * observable in any response — both requests answer `200` with the same
 * checklist — so unlike the other no-ops in this file it needs no detection at
 * all: there is no event to suppress. `ON CONFLICT … DO UPDATE` is the whole
 * of it.
 */
export const checklistHideCheckedPutSchema = z.strictObject({
  hidden: z
    .boolean()
    .describe(
      'Whether the CALLING account hides this checklist’s checked items ' +
        '(FR-CARD-018). Per user per checklist: it changes nothing another ' +
        'member sees, and it changes nothing the server sends — the items ' +
        'array is always complete, and this flag is a rendering instruction.',
    ),
});

export type ChecklistHideCheckedPut = z.infer<typeof checklistHideCheckedPutSchema>;

/**
 * `POST /checklists/:id/items` — `FR-CARD-017`, the add-item input, including
 * its multi-line paste.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ ONE FIELD, N ITEMS — `text` IS A **PASTE**, NOT AN ITEM
 * ---------------------------------------------------------------------------
 * `FR-CARD-017` gives this input two behaviours through one control: typing a
 * line and pressing Enter creates one item (AC1), and pasting three lines
 * creates three (AC2). They are the same request with a different number of
 * line breaks in it, so this body has **one** field and no `items: string[]`
 * alternative — a client that has to decide which shape to send would have to
 * re-implement the split, and `FE-26.1`'s split and the server's would drift
 * with nothing to catch it. `splitChecklistItemLines` is the shared rule; this
 * body is the only thing that applies it on the way in.
 *
 * ⚠ **The route therefore answers with an ARRAY even when one item was
 * created.** The response shape does not vary with N, so no client branches on
 * it — see `checklistItemListSchema`.
 *
 * ---------------------------------------------------------------------------
 * THREE REFUSALS, ALL STRUCTURAL RATHER THAN LEFT TO THE HANDLER
 * ---------------------------------------------------------------------------
 * The refinement runs `splitChecklistItemLines` and refuses:
 *
 *   1. **A body with no surviving line** — `''`, `'   '`, `'\n\n\n'`.
 *      `FR-CARD-017`'s edge case is "an empty item cannot be created; blank
 *      input is ignored", and *ignored* is the trap: it means the blank
 *      **lines** are skipped, not that a wholly blank request succeeds. A
 *      `201` with `[]` would tell a client its paste worked when nothing was
 *      created, so this is a **422**, never an empty-array success.
 *   2. **A line over `CHECKLIST_ITEM_TEXT_MAX_LENGTH`** — the column bound,
 *      per line rather than per body, naming the offending line's number.
 *      Note what this means for the field itself: `text` carries **no
 *      `.max()`**, because a legitimate forty-line paste is far longer than
 *      any one item may be, and bounding the body at the column width would
 *      refuse the requirement's own headline gesture.
 *   3. **More than `CHECKLIST_ITEM_PASTE_MAX_ITEMS` lines** — a request-shape
 *      bound; see that constant for why one is owed here and not on
 *      `./board-filter`'s facets.
 *
 * All three are refinements rather than handler code on `./comment`'s
 * `decodeCommentCursor` reasoning: a refusal that depends on the handler
 * remembering to check is a refusal that stops existing the day somebody
 * writes a second call site. Each issue is reported on `path: ['text']` with a
 * message that names what was wrong, because `app.ts` maps only
 * `issue.message` into `details`.
 *
 * ⚠ **Each surviving line becomes its own `checklist.item_added` event** — N
 * lines, N rows. That is deliberately *not* the copy's shape
 * (`sourceChecklistId` on `POST /cards/:id/checklists` writes one
 * `checklist.added` and no item events at all): a paste is N things a member
 * typed, a copy is one gesture. `./events`' B26 header argues the asymmetry in
 * full.
 */
export const checklistItemCreateBodySchema = z
  .strictObject({
    text: z
      .string()
      .describe(
        'One item, or a multi-line paste creating one item per line ' +
          '(FR-CARD-017). Blank lines are skipped; a body with no non-blank ' +
          'line is refused (422) rather than answered with an empty array. ' +
          'Each line is trimmed and must fit checklist_items.text; the body ' +
          'itself is not bounded by that width, because a paste is many items.',
      ),
  })
  .superRefine((body, ctx) => {
    const lines = splitChecklistItemLines(body.text);

    if (lines.length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['text'],
        message:
          'text must contain at least one non-blank line: blank lines are skipped ' +
          '(FR-CARD-017), so a body that is empty or entirely whitespace would create ' +
          'no items at all.',
      });
      return;
    }

    if (lines.length > CHECKLIST_ITEM_PASTE_MAX_ITEMS) {
      ctx.addIssue({
        code: 'custom',
        path: ['text'],
        message:
          `text has ${lines.length} non-blank lines and at most ` +
          `${CHECKLIST_ITEM_PASTE_MAX_ITEMS} items may be created in one request. ` +
          'Send the rest in a further request — a checklist itself has no item limit.',
      });
      return;
    }

    const overlong = lines.findIndex(
      (line) => line.length > CHECKLIST_ITEM_TEXT_MAX_LENGTH,
    );
    if (overlong !== -1) {
      ctx.addIssue({
        code: 'custom',
        path: ['text'],
        message:
          `line ${overlong + 1} is ${lines[overlong]?.length} characters after trimming ` +
          `and an item holds at most ${CHECKLIST_ITEM_TEXT_MAX_LENGTH}.`,
      });
    }
  });

export type ChecklistItemCreateBody = z.infer<typeof checklistItemCreateBodySchema>;

/**
 * `PATCH /checklist-items/:id/text` — `FR-CARD-017`, "items are editable
 * inline". SC-4, field-scoped.
 *
 * ⚠ **One item, and the paste rule does NOT apply here.** This field is
 * `checklistItemTextSchema` verbatim — trimmed, non-empty, bounded by the
 * column — so an edit containing a newline is one item whose text contains a
 * newline, not two items. `FR-CARD-017`'s "one item per line" is a property of
 * the **add** input ("pasting multi-line text creates one item per line"), and
 * an edit that silently split into two rows would be a create the member did
 * not ask for, on a route whose whole subject is one existing row.
 * `cardTitleSchema`'s note about interior newlines is the same call one table
 * over.
 *
 * ⚠ **An edit to the text the item already carries writes no activity row** —
 * compared **after trimming** against the pre-image inside the transaction,
 * never against the raw body. See `checklistTitlePatchSchema`, and note that
 * `checklist_items.updated_at` is trigger-owned and will not betray a no-op
 * either.
 */
export const checklistItemTextPatchSchema = z.strictObject({
  text: checklistItemTextSchema.describe(
    'The item’s new text. Trimmed; an empty or whitespace-only submission is ' +
      'refused (422). A newline here does NOT split the item — the one-item- ' +
      'per-line rule belongs to the add-item input, not to an edit.',
  ),
});

export type ChecklistItemTextPatch = z.infer<typeof checklistItemTextPatchSchema>;

/**
 * `PATCH /checklist-items/:id/checked` — `FR-CARD-017` AC3, the checkbox.
 * SC-4, field-scoped.
 *
 * ⚠ **A boolean, not two routes and not a toggle.** The client sends the state
 * it wants rather than "flip it", because two members racing one checkbox must
 * converge: a flip is a read-modify-write whose two orderings give two
 * different answers, while an absolute value gives the same answer whichever
 * lands last (`FR-EXP-031`'s per-field last-write-wins). The **event** is
 * still split in two — `checklist.item_completed` and
 * `checklist.item_uncompleted`, one name per direction — because a feed must
 * render them as different sentences; the type carries the direction, so
 * neither row needs a `checked` field.
 *
 * ⚠ **Ticking an already-ticked item is a legal no-op that emits nothing**,
 * decided by `UPDATE … WHERE checked <> $next RETURNING` rather than
 * SELECT-then-write — `due.date_completed`'s precedent exactly, and the losing
 * side of the race is then a `200` describing the state both members wanted
 * rather than a phantom tick in an append-only feed.
 *
 * ⚠ **Ticking an item is not completing the CARD.** `FR-CARD-017`'s edge case
 * is explicit that the two are independent (`FR-CARD-004`), so nothing here
 * touches `cards.completed` or emits `due.date_completed`, and a checklist
 * reaching 100% leaves the card's own completion exactly where it was.
 */
export const checklistItemCheckedPatchSchema = z.strictObject({
  checked: z
    .boolean()
    .describe(
      'Whether the item is ticked (FR-CARD-017 AC3). An absolute state, never ' +
        'a toggle, so two members clicking one checkbox converge. Setting it ' +
        'to the value it already holds is a legal no-op and records nothing.',
    ),
});

export type ChecklistItemCheckedPatch = z.infer<typeof checklistItemCheckedPatchSchema>;

export const checklistItemAssigneePatchSchema = z.strictObject({
  assigneeUserId: ulidSchema
    .nullable()
    .describe(
      'The board member assigned to this checklist item (FR-CARD-020), or null ' +
        'to clear the assignment. The id must name a live member of the item’s board.',
    ),
});

export type ChecklistItemAssigneePatch = z.infer<typeof checklistItemAssigneePatchSchema>;

export const checklistItemDueDatePatchSchema = z.strictObject({
  dueAt: cardDateInstantSchema
    .nullable()
    .describe(
      'The checklist item due date and time as an absolute instant — an ISO-8601 ' +
        'string with an explicit offset or a trailing "Z" (FR-CARD-020). Send ' +
        'null to clear it.',
    ),
});

export type ChecklistItemDueDatePatch = z.infer<typeof checklistItemDueDatePatchSchema>;

/**
 * `POST /checklist-items/:id/move` — `FR-CARD-017` AC4, the drag: "given an
 * item in checklist A, when the user drags it into checklist B on the same
 * card, then it is removed from A, appears in B at the drop position, and both
 * progress counters update."
 *
 * ---------------------------------------------------------------------------
 * ONE ROUTE, TWO GESTURES, TWO EVENT NAMES — B12'S DECISION INHERITED WHOLE
 * ---------------------------------------------------------------------------
 * This body serves a reorder **within** a checklist and a move **between** two
 * checklists on the same card, exactly as `POST /cards/:id/move` serves both
 * of B12's, and for the same reason: a client dragging cannot know which one
 * it performed — a drag that starts in checklist A and ends in checklist A is
 * the same drop as one that ends in B. The **feed** must still tell them
 * apart, so the route emits `checklist.item_position_changed` or
 * `checklist.item_moved` accordingly (D5, and `./events` argues it at length);
 * the discriminator is the event type and there is deliberately no `kind`
 * field here or there.
 *
 * `checklistId` is therefore **required**, with no default and no nullable. A
 * body that omitted it would mean "reorder within whatever checklist this item
 * is in", which is a second implicit gesture the route would have to branch
 * on, and it would make the between-checklist move the only one that names its
 * destination — the shape that grows into two routes later.
 *
 * ---------------------------------------------------------------------------
 * ⚠⚠ BOTH NEIGHBOURS NULL IS **LEGAL** HERE AND IS A 422 ON
 * `checklistMoveBodySchema` ABOVE. READ BOTH BEFORE HARMONISING EITHER.
 * ---------------------------------------------------------------------------
 * There the pair meant "move this checklist to nowhere", because that body
 * carries no destination. Here the destination is `checklistId`, so the same
 * pair says something complete and ordinary: **"the bottom of that
 * checklist"** — and it has to, because `FR-CARD-017` AC4's checklist B may be
 * **empty**, in which case there is no neighbour to name. Refusing the pair
 * for symmetry would make the requirement's own worked example unexpressible
 * whenever the destination is empty, and force a client to special-case
 * "dropped below every item". This is `cardMoveBodySchema`'s reading in
 * `./card`, whose header calls the same divergence "the most harmonisable-
 * looking thing in either file".
 *
 * ---------------------------------------------------------------------------
 * ⚠ NO `cardId`, AND THE ABSENCE IS ENFORCEMENT RATHER THAN OMISSION
 * ---------------------------------------------------------------------------
 * `FR-CARD-017` confines the drag to "checklists on the **same card**". A Zod
 * schema cannot express "a checklist on the same card as this item"; what it
 * can do is refuse to carry a card id, so this body cannot even *ask* for a
 * cross-card move. The route answers the same `404` for a destination
 * checklist on another card that it answers for one that does not exist — a
 * caller must not learn that a checklist exists on a card they were not
 * addressing. `cardMoveBodySchema`'s refusal of a `boardId` is the identical
 * device one level up, and `checklist.item_moved`'s docstring records the
 * consequence: the event's two checklist ids always belong to one card, which
 * is what lets `cardId` and `boardId` stay single values on the columns.
 *
 * ⚠ **A drop in place emits nothing** — detected against the resolved gap, not
 * the raw body. See `checklistMoveBodySchema`.
 */
export const checklistItemMoveBodySchema = z.strictObject({
  checklistId: ulidSchema.describe(
    'The destination checklist — this item’s own for a reorder, a sibling on ' +
      'the SAME card for a move (FR-CARD-017 AC4). Required: a drag always ' +
      'lands in a named checklist. A checklist on another card is refused with ' +
      'the same 404 a nonexistent one gets.',
  ),
  previousItemId: checklistItemNeighbourSchema.describe(
    'The item immediately ABOVE the gap this item is dropped into. Both ' +
      'neighbours null is legal and means the bottom of the destination ' +
      'checklist — which is what dropping into an EMPTY checklist produces.',
  ),
  nextItemId: checklistItemNeighbourSchema.describe(
    'The item immediately BELOW the gap this item is dropped into. Send it ' +
      'alone to move the item to the top of the destination checklist.',
  ),
});

export type ChecklistItemMoveBody = z.infer<typeof checklistItemMoveBodySchema>;

export const checklistItemConvertToCardBodySchema = z.strictObject({
  listId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The destination list on the same board. Null or omitted means the source card’s current list.',
    ),
  previousCardId: ulidSchema
    .nullable()
    .default(null)
    .describe(
      'The card immediately above the new card. Both neighbours null means append.',
    ),
  nextCardId: ulidSchema
    .nullable()
    .default(null)
    .describe('The card immediately below the new card.'),
});

export type ChecklistItemConvertToCardBody = z.infer<
  typeof checklistItemConvertToCardBodySchema
>;

export const checklistConvertedCardRefSchema = z.object({
  id: ulidSchema,
  title: z.string().max(CARD_TITLE_MAX_LENGTH),
  path: z.string().min(1),
});

export type ChecklistConvertedCardRef = z.infer<typeof checklistConvertedCardRefSchema>;

// ---------------------------------------------------------------------------
// Views — what a route RETURNS.
//
// `z.object`, so an unknown key is **stripped** rather than refused: an
// assertion that a field is absent from one of these must go through
// `Object.keys(schema.shape)`, because `safeParse` on an object carrying the
// extra key **succeeds** (`./notification`'s recorded trap, and
// `./board-archive` restates it).
//
// Width-bounded strings, no format checks, no transforms — a view describes a
// ROW and must not refuse one a fixture, an import or an earlier product rule
// legitimately wrote.
// ---------------------------------------------------------------------------

/**
 * One checklist item — `FR-CARD-017`.
 *
 * It appears on the wire inside `checklistSchema.items` and as the element of
 * `checklistItemListSchema` — the receipt `POST /checklists/:id/items` answers
 * with. **No READ returns one independently of its checklist**: there is no
 * `GET /checklist-items/:id` and no listing across checklists, and the three
 * item mutations each answer with the single item they touched.
 *
 * ⚠ **No `cardId`.** It is recoverable from the checklist that owns it, which
 * carries its own — `cardLabelSchema`'s narrowing rule applied to a field that
 * would be constant across every element of the array it rides in.
 *
 * `assigneeUserId` and `dueAt` are B62's `FR-CARD-020` fields. Copies still
 * omit both columns on insert, so `FR-CARD-019`'s "text and order only" rule
 * stays true even though the read shape can now express the fields.
 *
 * `convertedCard` is B62.2's `FR-CARD-021` link. It is a small ref rather than
 * `convertedToCardId` alone so a read after a card rename reflects the card's
 * current title and permalink.
 */
export const checklistItemSchema = z.object({
  id: ulidSchema,
  /**
   * `checklist_items.checklist_id char(26) NOT NULL`,
   * `fk_checklist_items_checklist_id` referencing `checklists(id)` **ON DELETE
   * CASCADE**.
   *
   * On the wire even though the item is nested inside its checklist, because
   * `POST /checklists/:id/items` answers with a bare array of these and a
   * client applying that response to its cache needs to know where the items
   * go without re-reading the request it sent.
   *
   * ⚠ The cascade is why `DELETE /checklists/:id` emits **one**
   * `checklist.removed` and no per-item storm: the items go in the database
   * and the application never sees the rows.
   */
  checklistId: ulidSchema,
  /**
   * `checklist_items.text varchar(2048) NOT NULL`.
   *
   * ⚠ **Width-bounded only: no `.trim()`, no `.min(1)`.** The input primitive
   * trims and refuses blank; this one describes a row. That is the input/view
   * split this package is arranged around — `labelSchema.name`'s note, and
   * `cardDetailSchema.description`'s sharper version of it.
   */
  text: z.string().max(CHECKLIST_ITEM_TEXT_MAX_LENGTH),
  /**
   * `checklist_items.checked boolean NOT NULL DEFAULT false` — `FR-CARD-017`
   * AC3.
   *
   * ⚠ **A copied item is always `false`**, whatever the source's state
   * (`FR-CARD-019` AC2, this block's D2). And ticking an item is independent
   * of `cardSchema.completed`: `FR-CARD-017`'s edge case keeps the two apart
   * explicitly.
   */
  checked: z.boolean(),
  assigneeUserId: ulidSchema.nullable(),
  dueAt: z.string().min(1).nullable(),
  convertedCard: checklistConvertedCardRefSchema.nullable(),
  /**
   * `checklist_items.position varchar(64) COLLATE "C" NOT NULL` — the
   * fractional rank that fixes item order within one checklist.
   *
   * On the wire, on `listSchema.position`'s and `labelSchema.position`'s
   * precedent: a client needs it to place an optimistically-created item
   * before the server answers. ⚠ **A client never computes one** (SC-2) — no
   * body in this file accepts a rank, and `strictObject` refuses a `position`
   * key outright.
   *
   * ⚠ The array is always served `ORDER BY position, id`. The `id` tie-break
   * is not decoration: two ranks can collide after enough subdivision, and
   * without it two members would see one checklist in two orders with nothing
   * failing.
   */
  position: z.string().max(RANK_MAX_LENGTH),
  /**
   * `checklist_items.created_at timestamptz(3) NOT NULL DEFAULT now()`.
   *
   * A bounded string rather than `z.iso.datetime()` or a coerced `Date`, for
   * the reason every timestamp in this package states: `pg` hands a
   * `timestamptz` back in **Postgres' own spelling**
   * (`2026-09-01 09:00:00.123+00`), which is a legitimate instant and not an
   * ISO-8601 `T`/`Z` form, so pinning the serialisation here would 500 every
   * response that carries a row. `./card-dates`' header explains why the
   * *input* side is the opposite (strict ISO with an offset): a client should
   * never be echoing Postgres' spelling back as a request.
   *
   * ⚠ **It is not the item's order.** Ordering is `position`; `created_at` is
   * the audit timestamp, and sorting by it would silently undo every reorder
   * (`FR-CARD-009` AC2's mistake in a second costume).
   */
  createdAt: z.string().min(1),
  /**
   * `checklist_items.updated_at`, owned by `trg_checklist_items_set_updated_at`.
   *
   * ⚠ **Nothing in the application ever writes this column**, and the trigger
   * fires `WHEN (OLD.* IS DISTINCT FROM NEW.*)` — so an edit that changes
   * nothing leaves this value byte-identical. That is correct and must not be
   * "fixed": an application `set` beside it would be a second writer of a
   * trigger-derived value, the mistake this repository names three times.
   * `comments.edited_at` is the documented exception to that rule and is not a
   * precedent — an item has no "(edited)" marker to feed (`./events`'
   * `checklist.item_edited` argues why, and why the feed carries the edit
   * instead).
   */
  updatedAt: z.string().min(1),
});

export type ChecklistItem = z.infer<typeof checklistItemSchema>;

/**
 * One checklist and everything on it — `FR-CARD-016`, `FR-CARD-017`,
 * `FR-CARD-018`.
 *
 * The response of `POST /cards/:id/checklists`, `PATCH /checklists/:id/title`,
 * `POST /checklists/:id/move` and `PUT /checklists/:id/hide-checked`, the
 * element of `GET /cards/:id/checklists`, and the element of
 * `cardDetailSchema.checklists` on the card back. (`DELETE /checklists/:id`
 * answers `204` with no body, so it needs no shape — `DELETE /lists/:id`'s
 * precedent.)
 *
 * One view for all of them rather than a narrower shape per route, because
 * they all answer the same question — *what is this checklist now* — and a
 * client that has just renamed one needs exactly what a client that has just
 * listed them needs. `listSchema`'s and `labelSchema`'s argument.
 *
 * ⚠ **No `itemCount`, no `checkedItemCount`** — both are `items`' own, and the
 * module header argues at length why they are absent, where the counts that
 * *cannot* be derived live instead (`boardFaceCardSchema`, `./board-contents`)
 * and what change would require them back.
 */
export const checklistSchema = z.object({
  id: ulidSchema,
  /**
   * `checklists.card_id char(26) NOT NULL`, `fk_checklists_card_id`
   * referencing `cards(id)` **ON DELETE CASCADE**.
   *
   * On the wire because `GET /cards/:id/checklists` and the card back both
   * serve arrays of these into a client cache that is keyed by card, and
   * because `POST /checklist-items/:id/move` is confined to one card — a
   * client validating a drop target locally needs the two checklists' cards to
   * compare.
   *
   * ⚠ The cascade is a soft-delete detail worth knowing: **no route in
   * `apps/server/src` hard-deletes a `cards` row** — every card delete since
   * B22 sets `deleted_at` — so this cascade fires only when a board is
   * genuinely destroyed, and a checklist is otherwise only ever removed by its
   * own route. Measured rather than assumed, twice: B26 stage 1 checked it
   * against the whole server tree, and it was re-grepped for this file
   * (`delete(cards)` / `DELETE FROM cards`, no hits). ⚠ It is a statement
   * about the code as it stands, not an invariant anything enforces — a later
   * block that adds a hard card delete takes its checklists and their items
   * with it, silently, and nothing here would fail.
   */
  cardId: ulidSchema,
  /**
   * `checklists.title varchar(255) NOT NULL`.
   *
   * Width-bounded only — see `checklistItemSchema.text`. Never null and never
   * `''`: the column is `NOT NULL` and `checklistTitleSchema` refuses blank on
   * the way in, so "unnamed" is not a state a checklist has (which is the
   * opposite of `labelSchema.name`, and the reason that field is nullable and
   * this one is not).
   */
  title: z.string().max(CHECKLIST_TITLE_MAX_LENGTH),
  /**
   * `checklists.position varchar(64) COLLATE "C" NOT NULL` — the fractional
   * rank fixing this checklist's order among the card's others. Always served
   * `ORDER BY position, id`; a client never computes one (SC-2). See
   * `checklistItemSchema.position`.
   */
  position: z.string().max(RANK_MAX_LENGTH),
  /**
   * Whether the **calling account** hides this checklist's checked items —
   * `FR-CARD-018`, read from `checklist_hide_checked_prefs` on
   * `(user_id, checklist_id)`.
   *
   * ⚠⚠ **The one field in this package whose value depends on WHO ASKED.**
   * Two members reading one checklist legitimately receive different bytes for
   * this row; the module header records the two consequences (it is not
   * cacheable across accounts, and `false` covers both "no preference row" and
   * "anonymous caller" as one deliberately indistinguishable state).
   *
   * ⚠ **It filters nothing.** `items` below is always the complete set,
   * checked and unchecked alike, in rank order. This flag is a rendering
   * instruction for `FE-26.2`, which the requirement's own words demand: the
   * toggle "removes checked items from view **without changing their state**"
   * and "indicates **how many** are hidden" — a number a client can only
   * produce from items it was actually sent. A server that filtered here would
   * also make `items.length` stop being the checklist's total, which is what
   * both progress counters are computed from.
   */
  hideChecked: z.boolean(),
  /**
   * This checklist's items, **complete and in rank order** (`position, id`).
   *
   * ⚠ **Required on every response carrying a checklist, never optional and
   * never omitted "because the client has it".** Both of `FR-CARD-018`'s
   * counters are derived from this array and from nothing else, so a route
   * that answered without it would ship a checklist whose progress reads 0/4.
   * A missing required key is a genuine parse failure — unlike an *extra* key,
   * which `z.object` strips silently — so this one fails loudly, which is the
   * whole reason the counts could be dropped.
   *
   * `[]` is an ordinary value: `FR-CARD-016` AC1 creates a checklist "with an
   * empty item list", and `FR-CARD-019` AC4 does the same when no source is
   * chosen. It can never mean "you may not see this" — that is a 404, decided
   * before this array is read at all.
   */
  items: z.array(checklistItemSchema),
  /**
   * `checklists.created_at timestamptz(3) NOT NULL DEFAULT now()`. A bounded
   * string, not a strict ISO check — see `checklistItemSchema.createdAt`.
   *
   * ⚠ **It is not the checklist's order**, even though `FR-CARD-016` says
   * checklists are "ordered as created": that sentence describes where a *new*
   * checklist is placed (appended), and `position` is what order means from
   * then on. Sorting by this would silently undo every reorder.
   */
  createdAt: z.string().min(1),
  /**
   * `checklists.updated_at`, owned by `trg_checklists_set_updated_at` — see
   * `checklistItemSchema.updatedAt`. Nothing in the application writes it, and
   * a rename to the title already held leaves it byte-identical.
   *
   * ⚠ **It does not move when an item changes.** The trigger is on
   * `checklists`, and adding, ticking or deleting an item updates
   * `checklist_items` only — so a client cannot use this field to decide
   * whether its cached items are stale.
   */
  updatedAt: z.string().min(1),
});

export type Checklist = z.infer<typeof checklistSchema>;

/**
 * `GET /cards/:id/checklists` — a card's checklists in card order
 * (`ORDER BY position, id`), each carrying its items.
 *
 * A **bare array**, mirroring `labelListSchema` at `GET /boards/:id/labels`
 * and `listListSchema` at `GET /boards/:id/lists`, not
 * `{ checklists: [...] }`.
 *
 * `[]` is a legitimate and extremely common value — every card in the product
 * has none until somebody adds one — and it can **never** mean "you may not
 * see this card": that is a 404, decided by the permission statement before
 * this array is read.
 *
 * ⚠ **This route exists beside `cardDetailSchema.checklists` deliberately.**
 * The card back carries the same data, so this is not the only way to read it;
 * it is the way to *re-read* it after a mutation without re-fetching a whole
 * card back, which `FE-26.1`'s optimistic reconciliation needs.
 */
export const checklistListSchema = z.array(checklistSchema);

export type ChecklistList = z.infer<typeof checklistListSchema>;

/**
 * `POST /checklists/:id/items` — the items **this request created**, in the
 * order they were created, which is the order of the lines in the paste.
 *
 * ⚠ **An array even when one item was created**, and never the item alone:
 * `FR-CARD-017` gives one input two behaviours (Enter creates one, a paste
 * creates N), so a response shape that varied with N would make every client
 * branch on a distinction the requirement treats as one gesture. AC1's single
 * item arrives as a one-element array.
 *
 * ⚠ **It is not the checklist's whole item list.** A client applying this
 * response appends; a client that replaced its cached array with it would drop
 * every item the checklist already held. `GET /cards/:id/checklists` is the
 * shape that answers "everything on this checklist now".
 *
 * `[]` is **not** a value this route can produce: a body with no non-blank
 * line is a 422 from `checklistItemCreateBodySchema` rather than a `201` with
 * an empty array, precisely so that a client never has to tell "nothing was
 * created" from "everything was created" by inspecting a length.
 */
export const checklistItemListSchema = z.array(checklistItemSchema);

export type ChecklistItemList = z.infer<typeof checklistItemListSchema>;
